import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect } from 'react'

import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger
} from '@/components/ui/combobox'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem
} from '@/components/ui/pagination'
import { pageWindow } from '@/lib/page-window'

/* shared "Showing X–Y of Z" + page-window controls for any paginated table.
 * page state is owned by the caller (server- or client-paginated alike). the
 * page size is required, not opt-in: only three tables ever passed it, and the
 * picker already hides itself on a short table. */
export function DataTablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 40, 50]
}: {
  page: number
  pageSize: number
  total: number
  onPageChange: (next: number) => void
  onPageSizeChange: (next: number) => void
  pageSizeOptions?: number[]
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  /* a page restored from the url can sit past the end once rows go away, e.g.
   * unfavoriting from a profile then pressing Back. */
  useEffect(() => {
    if (total > 0 && page > totalPages) onPageChange(totalPages)
  }, [total, page, totalPages, onPageChange])

  if (total === 0) return null

  /** below the smallest option the selector can't change anything, so hide it
   *  rather than offer a no-op. */
  const showPageSize = total >= Math.min(...pageSizeOptions)
  const rangeStart = (page - 1) * pageSize + 1
  const rangeEnd = Math.min(page * pageSize, total)
  const pages = pageWindow(page, totalPages)

  return (
    <div className='flex flex-col items-center justify-between gap-3 pt-2 sm:flex-row'>
      <div className='flex items-center gap-4 text-[12px] text-ink-muted'>
        <p>
          Showing <span className='text-ink-soft'>{rangeStart}</span>–
          <span className='text-ink-soft'>{rangeEnd}</span> of{' '}
          <span className='text-ink-soft'>{total}</span>
        </p>
        {showPageSize ? (
          <div className='flex items-center gap-2'>
            <span>Per page</span>
            <Combobox
              items={pageSizeOptions}
              value={pageSize}
              onValueChange={(next: number | null) =>
                next && onPageSizeChange(next)
              }
              itemToStringLabel={(size: number) => String(size)}
              itemToStringValue={(size: number) => String(size)}
            >
              <ComboboxTrigger className='inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-[12px] text-ink-soft'>
                {pageSize}
              </ComboboxTrigger>
              <ComboboxContent>
                <ComboboxList>
                  {(size: number) => (
                    <ComboboxItem key={size} value={size}>
                      {size}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </div>
        ) : null}
      </div>
      {totalPages > 1 ? (
        <Pagination className='mx-0 w-full justify-center sm:w-auto sm:justify-end'>
          <PaginationContent className='flex-wrap'>
            <PaginationItem>
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={page <= 1}
                aria-label='Previous page'
                onClick={() => onPageChange(page - 1)}
              >
                <ChevronLeft className='sm:hidden' />
                <span className='hidden sm:inline'>Previous</span>
              </Button>
            </PaginationItem>
            {pages.map((entry, index) => (
              <PaginationItem key={entry === 'gap' ? `gap-${index}` : entry}>
                {entry === 'gap' ? (
                  <PaginationEllipsis />
                ) : (
                  <Button
                    type='button'
                    variant={entry === page ? 'outline' : 'ghost'}
                    size='icon'
                    aria-current={entry === page ? 'page' : undefined}
                    onClick={() => onPageChange(entry)}
                  >
                    {entry}
                  </Button>
                )}
              </PaginationItem>
            ))}
            <PaginationItem>
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={page >= totalPages}
                aria-label='Next page'
                onClick={() => onPageChange(page + 1)}
              >
                <span className='hidden sm:inline'>Next</span>
                <ChevronRight className='sm:hidden' />
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      ) : null}
    </div>
  )
}
